/**
 * Pick string registry entries and append them to a template (Strapi repeatable `items`).
 * Step 1: select strings. Step 2: optional per-locale template overrides (defaults to global).
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useGlobalConfig } from "context/GlobalContext";
import { useTheme } from "@mui/material";
import { BglPrimarySmallButton, BglSecondarySmallButton } from "components/button/BglButtons";
import {
  StringRegistryApis
} from "../api/stringRegistryAPI";
import { ConfigRegistryApis } from "features/config-registry/api/configRegistryAPI";
import { StringRegistryItem, StringTemplate } from "../types/stringRegistryTypes";
import {
  getLocaleValue,
  normalizeLocaleCode,
  buildTemplateItemLocaleSummaryShort
} from "../utils/localeDisplay";
import { getGlobalLocaleOverrides } from "../utils/globalTemplateLocales";
import { SearchableSelect } from "components/common/SearchableSelect";

interface PublishedLocale {
  code: string;
  name: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  template: StringTemplate;
  /** Full member entry ids — required when template.items only holds the current page */
  memberEntryIds?: string[];
  onStringsAdded: () => void;
}

const STEPS = ["Select strings", "Optional overrides"];

export const AddStringsToTemplateDialog = ({ open, onClose, template, memberEntryIds, onStringsAdded }: Props) => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();

  const [activeStep, setActiveStep] = useState(0);

  const [strings, setStrings] = useState<StringRegistryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [localeCatalog, setLocaleCatalog] = useState<PublishedLocale[]>([]);
  const [localesLoading, setLocalesLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  const [searchDraft, setSearchDraft] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [verificationStatus, setVerificationStatus] = useState("");

  const [filterLocale, setFilterLocale] = useState("");
  const [filterLocalePresence, setFilterLocalePresence] = useState<"" | "has" | "missing">("");

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedEntryById, setSelectedEntryById] = useState<Map<string, StringRegistryItem>>(new Map());
  /** documentId -> normalizedLocale -> override draft (empty = inherit global) */
  const [importOverrides, setImportOverrides] = useState<Record<string, Record<string, string>>>({});

  const existingEntryIds = useMemo(() => {
    if (memberEntryIds?.length) return new Set(memberEntryIds);
    return new Set(
      (template.items || [])
        .map((it) => it.stringRegistryEntry?.documentId)
        .filter(Boolean) as string[]
    );
  }, [memberEntryIds, template.items]);

  const validLocaleCatalog = useMemo(
    () => localeCatalog.filter((l) => Boolean(normalizeLocaleCode(l?.code))),
    [localeCatalog]
  );

  const listLocaleParams = useMemo(() => {
    if (!filterLocale.trim() || !filterLocalePresence) return {};
    return { locale: filterLocale.trim(), localePresence: filterLocalePresence as "has" | "missing" };
  }, [filterLocale, filterLocalePresence]);

  const fetchLocales = useCallback(async () => {
    if (!open) return;
    setLocalesLoading(true);
    try {
      const res = await StringRegistryApis.getPublishedLocales(rc);
      if (res.error) {
        setLocaleCatalog([]);
        return;
      }
      const raw = res as { data?: unknown };
      const d = raw.data;
      setLocaleCatalog(Array.isArray(d) ? (d as PublishedLocale[]) : []);
    } catch {
      setLocaleCatalog([]);
    } finally {
      setLocalesLoading(false);
    }
  }, [open, rc]);

  const fetchStrings = useCallback(async () => {
    if (!open) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await StringRegistryApis.getStrings(rc, {
        page,
        pageSize,
        search: searchApplied.trim() || undefined,
        verificationStatus: verificationStatus || undefined,
        // Server-side exclusion — keeps the counts and page windows about what
        // is actually addable. Rows are still checked against existingEntryIds
        // below, since an older backend ignores this param.
        excludeTemplate: template.documentId,
        ...listLocaleParams
      });
      if (response.error) {
        setError(response.error.message || "Failed to load strings");
        setStrings([]);
        return;
      }
      const body = response as { data?: StringRegistryItem[]; meta?: { pagination?: { total?: number; pageCount?: number } } };
      setStrings(Array.isArray(body.data) ? body.data : []);
      const meta = body.meta?.pagination;
      if (meta) {
        setTotal(meta.total ?? 0);
        setPageCount(meta.pageCount ?? 1);
      }
    } catch (e: any) {
      setError(e.message || "Failed to load strings");
      setStrings([]);
    } finally {
      setIsLoading(false);
    }
  }, [open, rc, template.documentId, page, pageSize, searchApplied, verificationStatus, listLocaleParams]);

  useEffect(() => {
    fetchLocales();
  }, [fetchLocales]);

  useEffect(() => {
    fetchStrings();
  }, [fetchStrings]);

  // Entries already in the template stay VISIBLE but disabled. Dropping them
  // from the rows made a page whose every row is a member render blank while the
  // header still counted it as a full page — pagination is server-side, so the
  // page can't be back-filled client-side.
  const selectableStrings = useMemo(
    () => strings.filter((s) => !existingEntryIds.has(s.documentId)),
    [strings, existingEntryIds]
  );
  const memberCountOnPage = strings.length - selectableStrings.length;

  // A filter that shrinks the result set can leave `page` past the last page —
  // clamp so the grid never sits on an out-of-range blank page.
  useEffect(() => {
    if (isLoading) return;
    if (pageCount > 0 && page > pageCount) setPage(pageCount);
  }, [isLoading, page, pageCount]);

  const resetAndClose = () => {
    setActiveStep(0);
    setSelected(new Set());
    setSelectedEntryById(new Map());
    setImportOverrides({});
    setSearchDraft("");
    setSearchApplied("");
    setVerificationStatus("");
    setFilterLocale("");
    setFilterLocalePresence("");
    setPage(1);
    setError(null);
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setSearchDraft("");
    setSearchApplied("");
    setVerificationStatus("");
    setFilterLocale("");
    setFilterLocalePresence("");
    setPage(1);
    setSelected(new Set());
    setSelectedEntryById(new Map());
    setImportOverrides({});
    setError(null);
  }, [open, template.documentId]);

  const toggle = (row: StringRegistryItem) => {
    const id = row.documentId;
    if (existingEntryIds.has(id)) return;
    const next = new Set(selected);
    const map = new Map(selectedEntryById);
    if (next.has(id)) {
      next.delete(id);
      map.delete(id);
    } else {
      next.add(id);
      map.set(id, row);
    }
    setSelected(next);
    setSelectedEntryById(map);
  };

  // Over selectable rows only, so a page of already-added entries doesn't report
  // itself as "all selected"
  const selectedOnPage = selectableStrings.filter((s) => selected.has(s.documentId)).length;
  const allOnPage =
    selectableStrings.length > 0 && selectedOnPage === selectableStrings.length;
  const someOnPage = selectedOnPage > 0 && selectedOnPage < selectableStrings.length;

  const toggleSelectAllPage = () => {
    const next = new Set(selected);
    const map = new Map(selectedEntryById);
    if (allOnPage) {
      selectableStrings.forEach((s) => {
        next.delete(s.documentId);
        map.delete(s.documentId);
      });
    } else {
      selectableStrings.forEach((s) => {
        next.add(s.documentId);
        map.set(s.documentId, s);
      });
    }
    setSelected(next);
    setSelectedEntryById(map);
  };

  const handleSearchApply = () => {
    setSearchApplied(searchDraft);
    setPage(1);
  };

  const goToOverridesStep = () => {
    if (selected.size === 0) return;
    setImportOverrides((prev) => {
      const next = { ...prev };
      for (const id of selected) {
        if (!next[id]) next[id] = {};
      }
      return next;
    });
    setActiveStep(1);
  };

  const setOverrideCell = (docId: string, localeKey: string, value: string) => {
    setImportOverrides((prev) => ({
      ...prev,
      [docId]: { ...(prev[docId] || {}), [localeKey]: value }
    }));
  };

  const buildOverridesPayload = (docId: string) => {
    const row = importOverrides[docId] || {};
    return Object.entries(row)
      .map(([locale, value]) => ({ locale, value: value.trim() }))
      .filter((x) => x.locale && x.value !== "");
  };

  const handleFinalAdd = async () => {
    if (selected.size === 0) return;
    setIsSaving(true);
    setError(null);
    try {
      const values = Array.from(selected).map((documentId) => ({
        entryDocumentId: documentId,
        localeOverrides: buildOverridesPayload(documentId)
      }));
      const res = await StringRegistryApis.upsertTemplateValues(rc, template.documentId, values);
      if (res.error) {
        setError(res.error.message || "Update failed");
        return;
      }
      onStringsAdded();
      resetAndClose();
    } catch (e: any) {
      setError(e.message || "Update failed");
    } finally {
      setIsSaving(false);
    }
  };

  const selectedEntriesSorted = useMemo(() => {
    return Array.from(selectedEntryById.values()).sort((a, b) =>
      a.stringResourceId.localeCompare(b.stringResourceId)
    );
  }, [selectedEntryById]);

  const tableColCount = 6;

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { maxWidth: "96vw", height: "90vh" } }}
    >
      <DialogTitle>
        Add strings to template
        <Typography variant="body2" color="text.secondary">
          Append to &quot;{template.name}&quot;. Step 2 lets you set optional template overrides per locale; leave blank
          to use each string&apos;s global value.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
          <Typography variant="body2" component="span">
            Locale catalog comes from Strapi i18n (published). Overrides here are stored on the template item only.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && (
          <>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2, alignItems: "flex-end" }}>
              <TextField
                size="small"
                label="Search"
                placeholder="stringResourceId or display name"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearchApply()}
                sx={{ minWidth: 240, flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
              <SearchableSelect
                size="small"
                sx={{ minWidth: 180 }}
                label="Verification status"
                value={verificationStatus}
                onChange={(v) => {
                  setVerificationStatus(v);
                  setPage(1);
                }}
                options={[
                  { value: "", label: "Default (same as Strings list — approved-only for viewers)" },
                  { value: "APPROVED", label: "APPROVED" },
                  { value: "TODO", label: "TODO" },
                  { value: "READY_FOR_QA", label: "READY_FOR_QA" },
                  { value: "REJECTED", label: "REJECTED" }
                ]}
              />
              <SearchableSelect
                size="small"
                sx={{ minWidth: 160 }}
                disabled={localesLoading}
                label="Filter by locale"
                value={filterLocale}
                onChange={(v) => {
                  setFilterLocale(v);
                  if (!v) setFilterLocalePresence("");
                  else setFilterLocalePresence((prev) => prev || "has");
                  setPage(1);
                }}
                options={[{ value: "", label: "Any locale" }, ...validLocaleCatalog.map((l) => ({ value: l.code, label: `${l.name} (${l.code})` }))]}
              />
              <SearchableSelect
                size="small"
                sx={{ minWidth: 170 }}
                disabled={!filterLocale}
                label="Translation"
                value={filterLocalePresence}
                onChange={(v) => {
                  setFilterLocalePresence(v as "" | "has" | "missing");
                  setPage(1);
                }}
                options={[
                  { value: "", label: "Any" },
                  { value: "has", label: "Has value" },
                  { value: "missing", label: "Missing or empty" }
                ]}
              />
              <BglSecondarySmallButton onClick={handleSearchApply} disabled={isLoading}>
                Search
              </BglSecondarySmallButton>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Page {page} of {pageCount || 1} · {total} total · {selected.size} selected
                {localesLoading ? " · loading locale catalog…" : null}
              </Typography>
              <Chip
                label={
                  memberCountOnPage > 0
                    ? `${selectableStrings.length} addable · ${memberCountOnPage} already in template`
                    : `${selectableStrings.length} addable on this page`
                }
                size="small"
                variant="outlined"
              />
            </Box>

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440, overflowX: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ bgcolor: "background.paper" }}>
                        <Checkbox
                          indeterminate={someOnPage}
                          checked={allOnPage}
                          onChange={toggleSelectAllPage}
                          disabled={selectableStrings.length === 0}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "background.paper" }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "background.paper", minWidth: 140 }}>
                        stringResourceId
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "background.paper", minWidth: 120 }}>
                        Display name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "background.paper" }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: "background.paper", minWidth: 200 }}>
                        Locales (global preview)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {strings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={tableColCount} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No strings match these filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      strings.map((row, idx) => {
                        const isMember = existingEntryIds.has(row.documentId);
                        const isSel = selected.has(row.documentId);
                        const serial = (page - 1) * pageSize + idx + 1;
                        const preview = buildTemplateItemLocaleSummaryShort(
                          { stringRegistryEntry: row, localeOverrides: [] },
                          validLocaleCatalog,
                          200
                        );
                        return (
                          <TableRow
                            key={row.documentId}
                            hover={!isMember}
                            selected={isSel}
                            onClick={isMember ? undefined : () => toggle(row)}
                            sx={{ cursor: isMember ? "default" : "pointer", opacity: isMember ? 0.55 : 1 }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox checked={isSel} disabled={isMember} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {serial}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                                  {row.stringResourceId}
                                </Typography>
                                {isMember && (
                                  <Chip
                                    label="In template"
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: "0.65rem" }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap title={row.displayName}>
                                {row.displayName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={row.verificationStatus} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Tooltip title={preview}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }} noWrap>
                                  {preview}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <BglSecondarySmallButton onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || isLoading}>
                  Previous
                </BglSecondarySmallButton>
                <BglSecondarySmallButton
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  disabled={page >= pageCount || isLoading || pageCount === 0}
                >
                  Next
                </BglSecondarySmallButton>
              </Box>
              <SearchableSelect
                size="small"
                sx={{ minWidth: 100 }}
                label="Per page"
                value={String(pageSize)}
                onChange={(v) => {
                  if (!v) return;
                  setPageSize(Number(v));
                  setPage(1);
                }}
                options={[
                  { value: "25", label: "25" },
                  { value: "50", label: "50" },
                  { value: "100", label: "100" }
                ]}
              />
            </Box>

            {selected.size > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                Selections persist across pages ({selected.size} total). Use Next to optionally set template overrides
                before adding.
              </Typography>
            )}
          </>
        )}

        {activeStep === 1 && (
          <Box sx={{ maxHeight: "52vh", overflow: "auto", pr: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set template-specific text per locale when it should differ from the global string. Leave empty to
              inherit.
            </Typography>
            {selectedEntriesSorted.map((entry) => (
              <Accordion
                key={entry.documentId}
                defaultExpanded={selectedEntriesSorted.length === 1}
                disableGutters
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "8px !important",
                  mb: 1.5,
                  bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.01)",
                  boxShadow: "none",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": { mb: 1.5 }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    borderRadius: "8px",
                    px: 2,
                    minHeight: 48,
                    bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : theme.palette.action.hover,
                    "&.Mui-expanded": {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0
                    }
                  }}
                >
                  <Typography fontWeight={600} sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}>
                    {entry.stringResourceId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2, alignSelf: "center" }}>
                    {entry.displayName}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "transparent" }}>
                        <TableCell sx={{ fontWeight: 600, width: 140, color: "text.secondary", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px", pl: 2 }}>Locale</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Global value</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Template override</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validLocaleCatalog.map((loc) => {
                        const k = normalizeLocaleCode(loc.code);
                        const g = getLocaleValue(getGlobalLocaleOverrides(entry), loc.code) || "—";
                        const v = importOverrides[entry.documentId]?.[k] ?? "";
                        return (
                          <TableRow
                            key={k}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                              "&:hover": { bgcolor: theme.palette.action.hover }
                            }}
                          >
                            <TableCell sx={{ pl: 2, verticalAlign: "top", pt: 1.25 }}>
                              <Typography variant="body2" fontWeight={500}>{loc.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{k}</Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 220, verticalAlign: "top", pt: 1.25 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {g}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: 260, verticalAlign: "top", pr: 2 }}>
                              <TextField
                                size="small"
                                fullWidth
                                multiline
                                minRows={1}
                                maxRows={4}
                                placeholder="Optional — inherits global if blank"
                                value={v}
                                onChange={(e) => setOverrideCell(entry.documentId, k, e.target.value)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <BglSecondarySmallButton onClick={resetAndClose} disabled={isSaving}>
          Cancel
        </BglSecondarySmallButton>
        {activeStep === 1 ? (
          <>
            <BglSecondarySmallButton onClick={() => setActiveStep(0)} disabled={isSaving} startIcon={<ArrowBackIcon />}>
              Back
            </BglSecondarySmallButton>
            <BglPrimarySmallButton onClick={handleFinalAdd} disabled={isSaving || selected.size === 0}>
              {isSaving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              Add {selected.size} string{selected.size === 1 ? "" : "s"}
            </BglPrimarySmallButton>
          </>
        ) : (
          <BglPrimarySmallButton
            onClick={goToOverridesStep}
            disabled={selected.size === 0}
            endIcon={<ArrowForwardIcon />}
          >
            Next: optional overrides
          </BglPrimarySmallButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddStringsToTemplateDialog;
